import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Upload, GripVertical, Save } from 'lucide-react';

// DND Kit Imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sortable Category Card Component
const SortableCategoryCard = ({ category, onEdit, onDelete, isDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    position: 'relative',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative overflow-hidden rounded-xl border transition-all duration-300 ${
        isDragging 
          ? 'shadow-2xl scale-105 rotate-2 bg-white border-[#8b6f47]' 
          : 'shadow-sm hover:shadow-lg border-[#e6ddd3] bg-white hover:border-[#8b6f47]/30'
      }`}
      data-testid={`category-item-${category.id}`}
    >
      {/* Drag Handle */}
      <div 
        {...attributes} 
        {...listeners}
        className={`absolute top-3 right-3 z-20 p-2 rounded-lg cursor-grab active:cursor-grabbing transition-all duration-200 ${
          isDragging 
            ? 'bg-[#8b6f47] text-white shadow-lg' 
            : 'bg-white/80 text-[#8b6f47] hover:bg-[#8b6f47] hover:text-white backdrop-blur-sm'
        }`}
        title="Sürükle"
      >
        <GripVertical className="h-4 w-4" />
      </div>

      {/* Shine Effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out" />

      {/* Video/Image Content */}
      <div className="relative aspect-video overflow-hidden">
        {category.video_path ? (
          <video
            src={category.video_path}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            muted
            loop
            autoPlay
            playsInline
          />
        ) : category.image_path ? (
          <img
            src={category.image_path}
            alt={category.name_tr}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#f5f1eb] to-[#e6ddd3] flex items-center justify-center">
            <div className="text-center text-[#9a8478]">
              <div className="text-4xl mb-2">🍽️</div>
              <p className="text-sm font-medium">Medya Yok</p>
            </div>
          </div>
        )}
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="relative p-4">
        <div className="mb-3">
          <h3 className="font-bold text-lg mb-1 text-[#2d1f14] group-hover:text-[#8b6f47] transition-colors">
            {category.name_tr}
          </h3>
          <p className="text-sm text-[#6b5444] font-medium">
            {category.name_en}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onEdit(category)}
            className="flex-1 border-[#e6ddd3] hover:border-[#8b6f47] hover:bg-[#8b6f47] hover:text-white transition-all duration-200"
            data-testid={`edit-category-${category.id}`}
          >
            <Edit className="h-4 w-4 mr-1" /> 
            Düzenle
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => onDelete(category.id)}
            className="bg-red-500 hover:bg-red-600 border-red-500"
            data-testid={`delete-category-${category.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Dragging Indicator */}
      {isDragging && (
        <div className="absolute inset-0 bg-[#8b6f47]/10 border-2 border-dashed border-[#8b6f47] rounded-xl flex items-center justify-center">
          <div className="bg-[#8b6f47] text-white px-3 py-1 rounded-full text-sm font-medium">
            Sürükleniyor...
          </div>
        </div>
      )}
    </div>
  );
};

const CategoryManager = ({ token }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const [formData, setFormData] = useState({
    name_tr: '',
    name_en: '',
    video_path: '',
    image_path: '',
    background_images: []
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // DND Handlers
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      setCategories((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
      setHasChanged(true);
    }
  };

  const saveOrder = async () => {
    try {
      const categoryIds = categories.map(c => c.id);
      await axios.put(`${API}/admin/categories/reorder`, { category_ids: categoryIds }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Kategori sıralaması kaydedildi');
      setHasChanged(false);
    } catch (error) {
      console.error('Error saving category order:', error);
      toast.error('Sıralama kaydedilirken hata oluştu');
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Kategoriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      toast.error('Lütfen bir video dosyası seçin');
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/upload-video`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData(prev => ({ ...prev, video_path: response.data.video_path }));
      toast.success('Video yüklendi');
    } catch (error) {
      console.error('Video upload error:', error);
      toast.error('Video yüklenirken hata oluştu');
    } finally {
      setUploading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen bir resim dosyası seçin');
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/upload-image`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData(prev => ({ ...prev, image_path: response.data.image_path }));
      toast.success('Resim yüklendi');
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Resim yüklenirken hata oluştu');
    } finally {
      setUploading(false);
    }
  };

  const handleMultiImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen bir resim dosyası seçin');
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/upload-image`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData(prev => ({
        ...prev,
        background_images: [...(prev.background_images || []), response.data.image_path]
      }));
      toast.success('Resim eklendi');
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Resim yüklenirken hata oluştu');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      background_images: prev.background_images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingCategory) {
        await axios.put(`${API}/admin/categories/${editingCategory.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Kategori güncellendi');
      } else {
        await axios.post(`${API}/admin/categories`, { ...formData, order: categories.length }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Kategori oluşturuldu');
      }
      fetchCategories();
      resetForm();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Kategori kaydedilirken hata oluştu');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kategoriyi ve tüm ürünlerini silmek istediğinizden emin misiniz?')) return;

    try {
      await axios.delete(`${API}/admin/categories/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Kategori silindi');
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Kategori silinirken hata oluştu');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name_tr: category.name_tr,
      name_en: category.name_en,
      video_path: category.video_path || '',
      image_path: category.image_path || '',
      background_images: category.background_images || []
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name_tr: '',
      name_en: '',
      video_path: '',
      image_path: '',
      background_images: []
    });
    setEditingCategory(null);
    setDialogOpen(false);
  };

  if (loading && categories.length === 0) {
    return <div>Yükleniyor...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: '#2d1f14' }}>Kategoriler</h2>
          <p className="text-sm text-[#9a8478] mt-1">Kategorileri sürükleyerek sıralayabilirsiniz</p>
        </div>
        
        <div className="flex items-center gap-3">
          {hasChanged && (
            <Button
              onClick={saveOrder}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white"
            >
              <Save className="h-4 w-4" />
              Sıralamayı Kaydet
            </Button>
          )}
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  resetForm();
                  setDialogOpen(true);
                }}
                className="flex items-center gap-2"
                style={{ background: '#8b6f47', color: 'white' }}
                data-testid="add-category-button"
              >
                <Plus className="h-4 w-4" />
                Yeni Kategori
              </Button>
            </DialogTrigger>
            <DialogContent 
              className="max-w-2xl max-h-[90vh] overflow-y-auto"
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <DialogHeader>
                <DialogTitle>{editingCategory ? 'Kategori Düzenle' : 'Yeni Kategori Ekle'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name_tr">İsim (Türkçe)</Label>
                  <Input
                    id="name_tr"
                    value={formData.name_tr}
                    onChange={(e) => setFormData(prev => ({ ...prev, name_tr: e.target.value }))}
                    required
                    data-testid="category-name-tr-input"
                  />
                </div>
                <div>
                  <Label htmlFor="name_en">Name (English)</Label>
                  <Input
                    id="name_en"
                    value={formData.name_en}
                    onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
                    required
                    data-testid="category-name-en-input"
                  />
                </div>
              </div>



              <div>
                <Label htmlFor="video">Video</Label>
                <div className="flex items-center gap-4">
                  <Input
                    id="video"
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    disabled={uploading}
                    data-testid="category-video-input"
                  />
                  {uploading && <span className="text-sm">Yükleniyor...</span>}
                </div>
                {formData.video_path && (
                  <div className="relative mt-2 w-full max-w-xs">
                    <video src={formData.video_path} className="w-full rounded mb-2" controls />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => setFormData(prev => ({ ...prev, video_path: '' }))}
                      title="Videoyu Sil"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="image">Arka Plan Resmi</Label>
                <div className="flex items-center gap-4">
                  <Input
                    id="image"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploading}
                    data-testid="category-image-input"
                  />
                  {uploading && <span className="text-sm">Yükleniyor...</span>}
                </div>
                {formData.image_path && (
                  <div className="relative mt-2 w-full max-w-xs rounded overflow-hidden">
                    <img src={formData.image_path} alt="Background Preview" className="w-full h-auto" />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => setFormData(prev => ({ ...prev, image_path: '' }))}
                      title="Resmi Sil"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>



              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={resetForm}>İptal</Button>
                <Button type="submit" style={{ background: '#8b6f47', color: 'white' }} data-testid="save-category-button">
                  {editingCategory ? 'Güncelle' : 'Oluştur'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Drag & Drop Categories Grid */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={categories.map(c => c.id)}
          strategy={rectSortingStrategy}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((category) => (
              <SortableCategoryCard
                key={category.id}
                category={category}
                onEdit={handleEdit}
                onDelete={handleDelete}
                isDragging={activeId === category.id}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {categories.length === 0 && !loading && (
        <div className="text-center p-12 bg-gradient-to-br from-[#f5f1eb] to-[#e6ddd3] rounded-xl border-2 border-dashed border-[#8b6f47]/30">
          <div className="text-6xl mb-4">🍽️</div>
          <h3 className="text-lg font-semibold text-[#2d1f14] mb-2">Henüz kategori bulunmuyor</h3>
          <p className="text-[#9a8478] mb-4">İlk kategorinizi oluşturmak için yukarıdaki butonu kullanın</p>
        </div>
      )}
    </div>
  );
};

export default CategoryManager;
